import { Backdrop, Button, Stack } from '@mui/material';
import { useCloseUserAccountMutation } from '@schooly/api';
import { ApiError } from '@schooly/api';
import { useUserContext } from '@schooly/components/authentication';
import { useNotifications } from '@schooly/components/notifications';
import {
  CloseIcon,
  DeleteFilledIcon,
  ModalConfirm,
  ModalContent,
  ModalHeader,
  Spin,
} from '@schooly/style';
import { Auth } from 'aws-amplify';
import { FC, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form-lts';
import { FormattedMessage, useIntl } from 'react-intl';

import { ControlPasswordField } from '@schooly/components/form-text-field';
import { useFlag } from '@schooly/hooks/use-flag';

type CloseAccountForm = {
  password: string;
};

export const CloseAccountForm: FC = () => {
  const { user, logout } = useUserContext();
  const [isLoading, startLoading, stopLoading] = useFlag(false);
  const { showError } = useNotifications();
  const { formatMessage } = useIntl();

  const form = useForm<CloseAccountForm>({
    defaultValues: {
      password: '',
    },
  });

  const closeAccount = useCloseUserAccountMutation();
  const onSubmit = useCallback(
    async (data: CloseAccountForm) => {
      if (!user) return;

      startLoading();
      try {
        await Auth.signIn(user.account_email, data.password);
        await closeAccount.mutateAsync(user.user_id);
      } catch (err) {
        showError(err as ApiError);
      } finally {
        stopLoading();
      }
    },
    [user, closeAccount, showError, startLoading, stopLoading],
  );

  const isUserActionPrevented = isLoading || closeAccount.isLoading;

  return (
    <>
      <ModalContent active>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <Stack gap={2} alignItems="flex-end">
              <ControlPasswordField
                name="password"
                control={form.control}
                rules={{ required: true }}
                disabled={isUserActionPrevented}
                label={formatMessage({ id: 'login-YourPassword' })}
                fullWidth
              />
              <Button
                type="submit"
                color="error"
                startIcon={isUserActionPrevented ? <Spin /> : <DeleteFilledIcon />}
              >
                <FormattedMessage id="login-CloseMyAccount" />
              </Button>
            </Stack>
          </form>
        </FormProvider>
      </ModalContent>
      {isUserActionPrevented && <Backdrop open invisible />}
      {closeAccount.isSuccess && (
        <ModalConfirm open onClose={() => logout()}>
          <ModalHeader
            center
            multiline
            title={formatMessage({ id: 'login-SuccessfullyClosed' })}
            active
          />
          <ModalContent active>
            <Stack alignItems="center">
              <picture>
                <source srcSet={`/images/success.png, /images/success@2x.png 2x`} />
                <img
                  src={`/images/success.png`}
                  alt={formatMessage({ id: 'login-SuccessfullyClosed' })}
                />
              </picture>
              <Button size="large" onClick={() => logout()} startIcon={<CloseIcon />}>
                {formatMessage({ id: 'action-Continue' })}
              </Button>
            </Stack>
          </ModalContent>
        </ModalConfirm>
      )}
    </>
  );
};
